import { useEffect, useRef, useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface Stat {
  target: number;
  suffix: string;
  label: string;
  icon: string;
}

const STATS: Stat[] = [
  { target: 2500, suffix: '+', label: 'Staff Placed', icon: 'ri-group-line' },
  { target: 98, suffix: '%', label: 'Client Satisfaction', icon: 'ri-thumb-up-line' },
  { target: 15, suffix: '+', label: 'Years Experience', icon: 'ri-calendar-line' },
  { target: 500, suffix: '+', label: 'Active Clients', icon: 'ri-building-line' },
];

function AnimatedCounter({ target, suffix, isVisible }: { target: number; suffix: string; isVisible: boolean }) {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!isVisible || started.current) return;
    started.current = true;
    const duration = 1800;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + increment, target);
      setCount(Math.floor(current));
      if (current >= target) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, target]);

  return <>{count.toLocaleString()}{suffix}</>;
}

export default function StatsBar() {
  const { ref, isVisible } = useScrollAnimation(0.3);

  return (
    <section className="py-14 bg-[#03143A]">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((stat, i) => (
            <div
              key={i}
              className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <i className={`${stat.icon} text-[#C70532] text-2xl`} />
              </div>
              <div className="text-4xl md:text-5xl font-black text-white mb-1">
                <AnimatedCounter target={stat.target} suffix={stat.suffix} isVisible={isVisible} />
              </div>
              <p className="text-white/55 text-sm font-medium uppercase tracking-wide">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
